import { z } from "zod";
import { ExternalIdEntrySchema } from "./external-id.schema";

const commissionPlanStatuses = ["active", "inactive", "draft"] as const;

const JobTypeRuleSchema = z.object({
  job_type: z.string().describe("Job Type"),
  method: z.string().describe("Method"),
  rate: z.number().optional().describe("Rate"),
  min_job_size: z.number().optional().describe("Min Job Size"),
  fee_waived: z.boolean().optional().describe("Fee Waived"),
  tiers: z
    .array(
      z.object({
        min_gp_pct: z.number().describe("Min GP %"),
        rate: z.number().describe("Rate"),
      }),
    )
    .optional()
    .describe("Tiers"),
  bonus: z
    .object({
      threshold_gp_pct: z.number().describe("Threshold GP %"),
      bonus_rate: z.number().describe("Bonus Rate"),
    })
    .optional()
    .describe("Bonus"),
});
const ReferralBonusRuleSchema = z.object({
  min_value: z.number().describe("Min Value"),
  max_value: z.number().nullable().describe("Max Value"),
  bonus: z.number().describe("Bonus"),
});
const LatePenaltyRuleSchema = z.object({
  max_days: z.number().nullable().describe("Max Days"),
  penalty_pct: z.number().describe("Penalty %"),
});
export const CommissionPlanSchema = z.object({
  id: z.string().uuid().describe("ID"),
  business_id: z.string().uuid().describe("Business"),
  account_id: z.string().uuid().nullable().describe("Account"),
  brand_id: z.string().uuid().nullable().describe("Brand"),
  name: z.string().describe("Name"),
  effective_date: z.string().describe("Effective Date"),
  expires_date: z.string().nullable().describe("Expires Date"),
  status: z.enum(commissionPlanStatuses).describe("Status"),
  overhead_fee_pct: z.number().nullable().describe("Overhead Fee %"),
  job_type_rules: z.array(JobTypeRuleSchema).describe("Job Type Rules"),
  referral_bonus_rules: z
    .array(ReferralBonusRuleSchema)
    .nullable()
    .describe("Referral Bonus Rules"),
  late_penalty_rules: z
    .array(LatePenaltyRuleSchema)
    .nullable()
    .describe("Late Penalty Rules"),
  custom_fields: z
    .record(z.unknown())
    .nullable()
    .optional()
    .describe("Custom Fields"),
  created_at: z.string().nullable().describe("Created At"),
  updated_at: z.string().nullable().describe("Updated At"),
  external_ids: ExternalIdEntrySchema.array()
    .optional()
    .describe("External IDs"),
});
export type CommissionPlan = z.infer<typeof CommissionPlanSchema>;

export const CommissionPlanInsertSchema = z.object({
  name: z.string().describe("Name"),
  brand_id: z.string().uuid().nullable().optional().describe("Brand"),
  effective_date: z.string().describe("Effective Date"),
  expires_date: z.string().optional().describe("Expires Date"),
  status: z.enum(commissionPlanStatuses).default("draft").describe("Status"),
  overhead_fee_pct: z.number().optional().describe("Overhead Fee %"),
  job_type_rules: z.array(JobTypeRuleSchema).describe("Job Type Rules"),
  referral_bonus_rules: z
    .array(ReferralBonusRuleSchema)
    .optional()
    .describe("Referral Bonus Rules"),
  late_penalty_rules: z
    .array(LatePenaltyRuleSchema)
    .optional()
    .describe("Late Penalty Rules"),
  custom_fields: z.any().optional().describe("Custom Fields"),
});

export const CommissionPlanUpdateSchema = CommissionPlanInsertSchema.partial();

export const CommissionPlanQuerySchema = z.object({
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  status: z.enum(commissionPlanStatuses).optional(),
  effective_date: z.string().optional(),
  // schema-check-ignore: query-filter — null/"" semantically distinct from "no filter applied"
  brand_id: z.string().uuid().optional(),
});
